/**
 * Inline-SVG mini bar chart — no chart library dependency.
 *
 * Used alongside <Sparkline /> for cases where discrete buckets read
 * better than a continuous line: per-quarter benchmark drift, weekly
 * advisory counts per reinsurer, year-over-year metric changes. Pairs
 * with the existing Sparkline.tsx — same tone palette, same styling
 * pattern.
 *
 * Empty data renders a 1px dotted baseline + the empty-hint title.
 */
import { clsx } from 'clsx'

type Tone = 'rose' | 'emerald' | 'amber' | 'cyan' | 'violet' | 'blue' | 'muted'

interface BarChartProps {
  /** Bar values; same length as `labels` (when provided). */
  values:    number[]
  /** Optional per-bar labels (rendered in the title attribute). */
  labels?:   string[]
  width?:    number
  height?:   number
  /** Tailwind text colour class for the bars; default text-cyan. */
  tone?:     Tone
  className?: string
  emptyHint?: string
}

const TONE_FILL: Record<Tone, string> = {
  rose:    'text-rose-light',
  emerald: 'text-emerald-light',
  amber:   'text-amber-light',
  cyan:    'text-cyan',
  violet:  'text-violet',
  blue:    'text-blue',
  muted:   'text-text-muted',
}

export default function MiniBarChart({
  values,
  labels,
  width = 100,
  height = 22,
  tone = 'cyan',
  className = '',
  emptyHint = 'no data yet',
}: BarChartProps) {
  if (!values || values.length === 0) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`}
           className={clsx('text-text-muted', className)}
           preserveAspectRatio="none"
           aria-label={emptyHint}>
        <line x1={0} y1={height / 2} x2={width} y2={height / 2}
              stroke="currentColor" strokeOpacity="0.25" strokeDasharray="2,2" />
        <title>{emptyHint}</title>
      </svg>
    )
  }
  const pad = 1.5
  const max = Math.max(...values, 0)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const slot = (width - 2 * pad) / values.length
  const barWidth = Math.max(1, slot - 0.8)
  // Zero baseline — most series are non-negative, but allow signed values.
  const zeroY = height - pad - ((0 - min) / span) * (height - 2 * pad)
  return (
    <svg viewBox={`0 0 ${width} ${height}`}
         className={clsx(TONE_FILL[tone], className)}
         preserveAspectRatio="none">
      {min < 0 && max > 0 && (
        <line x1={pad} y1={zeroY} x2={width - pad} y2={zeroY}
              stroke="currentColor" strokeOpacity="0.18" strokeDasharray="2,2" />
      )}
      {values.map((v, i) => {
        const x = pad + i * slot + 0.4
        const yTop = height - pad - ((v - min) / span) * (height - 2 * pad)
        const h = Math.max(0.5, zeroY - yTop)
        const y = v >= 0 ? yTop : zeroY
        const label = labels?.[i] ? `${labels[i]}: ${v}` : String(v)
        return (
          <rect key={i} x={x} y={y} width={barWidth} height={h}
                fill="currentColor" fillOpacity="0.85">
            <title>{label}</title>
          </rect>
        )
      })}
    </svg>
  )
}
